import { BaseNumber, CheckedAddress, Hex, NormalizedNumber } from '@sparkdotfi/common-universal'
import { Address, erc20Abi } from 'viem'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'

import { sparkRewardsResponseSchema } from './sparkRewardsResponseSchema'
import { getChainConfigEntry } from '@/config/chain'
import { sparkRewardsConfig } from '@/config/contracts-generated'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { assets } from '@/ui/assets'

export interface RewardData {
  onchainMerkleRoot: Hex
  merkleRoot: Hex
  epoch: number
  rewardToken: Token
  cumulativeAmount: NormalizedNumber
  pendingAmount: NormalizedNumber
  restrictedCountryCodes: string[]
  preClaimed: NormalizedNumber
  merkleProof: Hex[]
  chainId: number
}

export interface FetchSparkRewardsParams {
  chainId: number
  wagmiConfig: Config
  account: Address
  rewardsContractAddress: Address
  createApiEndpoint: ({
    chainId,
    merkleRoot,
    account,
  }: {
    chainId: number
    merkleRoot: Hex
    account: Address
  }) => string
}

export async function fetchSparkRewards({
  chainId,
  wagmiConfig,
  account,
  rewardsContractAddress,
  createApiEndpoint,
}: FetchSparkRewardsParams): Promise<RewardData[]> {
  const expectedMerkleRoot = await readContract(wagmiConfig, {
    address: rewardsContractAddress,
    abi: sparkRewardsConfig.abi,
    functionName: 'merkleRoot',
    chainId,
  })

  const res = await fetch(createApiEndpoint({ chainId, merkleRoot: Hex(expectedMerkleRoot), account }))
  if (!res.ok) {
    throw new Error('Failed to fetch rewards')
  }

  const claimData = sparkRewardsResponseSchema.parse(await res.json())

  return Promise.all(
    claimData.map(async (claim) => {
      const { token_address, epoch } = claim

      const [symbol, name, decimals, preClaimed] = await Promise.all([
        readContract(wagmiConfig, {
          address: token_address,
          abi: erc20Abi,
          functionName: 'symbol',
          chainId,
        }),
        readContract(wagmiConfig, {
          address: token_address,
          abi: erc20Abi,
          functionName: 'name',
          chainId,
        }),
        readContract(wagmiConfig, {
          address: token_address,
          abi: erc20Abi,
          functionName: 'decimals',
          chainId,
        }),
        readContract(wagmiConfig, {
          address: rewardsContractAddress,
          abi: sparkRewardsConfig.abi,
          functionName: 'cumulativeClaimed',
          args: [account, token_address, BigInt(epoch)],
          chainId,
        }),
      ])

      const tokenSymbol = TokenSymbol(symbol)
      const knownToken = getChainConfigEntry(chainId).tokenDefinitionRepository.findBySymbol(tokenSymbol)
      const rewardToken = new Token({
        address: CheckedAddress(claim.token_address),
        symbol: tokenSymbol,
        name,
        decimals,
        logo: knownToken?.logo ?? assets.token.unknown,
        unitPriceUsd: claim.token_price ?? NormalizedNumber.ZERO,
      })
      return {
        onchainMerkleRoot: Hex(expectedMerkleRoot),
        merkleRoot: claim.root_hash,
        epoch: claim.epoch,
        rewardToken,
        cumulativeAmount: claim.claimable_amount_normalized,
        pendingAmount: claim.pending_amount_normalized,
        restrictedCountryCodes: claim.restricted_country_codes,
        preClaimed: rewardToken.fromBaseUnit(BaseNumber(preClaimed)),
        merkleProof: claim.proof,
        chainId,
      }
    }),
  )
}
